import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { logger } from '../lib/utils/logger';
import { Loader2 } from 'lucide-react';
import { APP_ROUTES } from '../config/routes';

/**
 * Props for the ProtectedRoute component
 * @interface ProtectedRouteProps
 * @property {ReactNode} children - The child components to be rendered if authenticated
 */
interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * A higher-order component that protects routes from unauthenticated access.
 *
 * This component checks if the user is authenticated. If not, it redirects to the login page.
 * While checking authentication status, it shows a loading spinner.
 *
 * @component
 * @example
 * ```tsx
 * <Route
 *   path="/protected"
 *   element={
 *     <ProtectedRoute>
 *       <Dashboard />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * @param {ProtectedRouteProps} props - The component props
 * @returns {JSX.Element} The protected content or a redirect to login
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading } = useAuthContext();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    logger.debug('Redirecting unauthenticated user to login');
    return <Navigate to={APP_ROUTES.LOGIN} />;
  }

  return <>{children}</>;
}
